/**
 * Teacher reward stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const teacherRewardStickersPack: ToilePack = {
    slug: 'teacher-reward-stickers',
    category: 'sticker',
    name: 'Teacher reward stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'star', label: 'Great job star', subject: 'a gold star lettered "GREAT JOB" across its middle'},
        {key: 'apple', label: 'Smiling apple', subject: 'a red apple with a smiling face lettered "WELL DONE" beneath'},
        {key: 'thumb', label: 'Thumbs up', subject: 'a cartoon thumbs-up hand lettered "NICE WORK"'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a trophy instead', 'a whole sticker sheet', 'in gold foil'],
    directionHint: 'try: a trophy instead',
    seo: {
        title: 'Teacher Reward Stickers: Star, Apple, Thumbs-Up',
        metaDescription: 'Nine teacher reward stickers — a great job star, a smiling apple, a thumbs-up — in kawaii, bold, and doodle styles.',
        h1: 'Teacher reward stickers: star, apple, thumbs-up in three styles',
        tagline: 'Nine reward stickers, three classroom favorites, three styles each.',
        intro: "The sticker on the worksheet, drawn three ways: a lettered great job star, a smiling apple, a thumbs-up. Each comes kawaii, bold, and doodle, so a teacher can match a whole classroom's look from one sheet. Ask for a different phrase — super effort, star student, nice work — and the same three styles carry it.",
        useCases: [
            'a classroom reward chart',
            'worksheet stickers for graded papers',
            'a sticker chart for good behavior',
            'homeschool incentive stickers',
            'a printable planner reward',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'The page is built for standard letter/A4 printing; each sticker prints cleanly at typical reward-sticker size (roughly 1-2 inches) or larger for a poster chart.'},
            {q: 'Do I get a full sheet or one sticker at a time?', a: 'The pack delivers nine individual designs on one page — print the whole sheet, or crop and print single stickers as needed.'},
            {q: 'Can I ask for different wording, like "super effort"?', a: 'Yes — the direction field takes any phrase, and the same kawaii, bold, and doodle styles carry it.'},
            {q: 'Can I use these on a classroom sticker chart?', a: 'Yes — that is exactly what these are made for, whether printed on paper or run through sticker-sheet stock.'},
            {q: 'Can I use these commercially, for a classroom shop or teacher store?', a: 'Personal and classroom use is fine; check the license terms before reselling the designs themselves.'},
        ],
    },
}
